/* ═══════════════════════════════════════════════════════════════
   dockLayout — the pure half of the dockable workspace's persistence.

   DESIGN.md §3.6: a docked panel is stored and applied as a FRACTION of
   its workspace with a floor and a cap, never a pixel width. So the
   persisted layout keeps no pixels at all: every `size` inside the
   serialized dockview grid is normalised to 0-1 along its own axis
   (width for a node laid out horizontally, height for a vertical one)
   and multiplied back out against whatever workspace it lands in.

   Everything here is pure — no DOM, no storage — so the one-time
   migration off the old pixel shape is unit-testable (dockLayout.test.ts).
   FormaDock.tsx owns the DOM half: measuring the workspace, the
   dial-aware floor/cap, and the localStorage/Prefs writes.
   ═══════════════════════════════════════════════════════════════ */

/** Persisted blob version. v2 = pixel sizes + the window they were saved at
 *  (rescaled by ratio on restore). v3 = fractions of the workspace. */
export const DOCK_BLOB_VERSION = 3;

/** A panel never takes more than this share of its workspace along the axis
 *  it docks on (DESIGN.md §3.6 cap). */
export const PANEL_CAP_FRACTION = 0.45;

/** Hard sanity floor for a grid node, in px — a saved layout can never
 *  denormalise into a zero-height sliver. The real, dial-aware panel floor
 *  is applied to live panels by FormaDock (`DEFAULT_MIN_PANEL_*` in rem). */
const MIN_NODE_PX = 40;

interface GridNode {
  type?: string;
  size?: number;
  data?: unknown;
}

/** Just the part of a serialized dockview layout this module touches. Every
 *  other key (panels, activeGroup, floatingGroups…) rides along untouched —
 *  the walk clones and returns the whole record. */
export interface DockLayout {
  grid?: {
    root?: GridNode;
    orientation?: string;
    width?: number;
    height?: number;
  };
}

export interface DockBlob {
  v: number;
  ts: number;
  /** Workspace size this layout describes. */
  w: number;
  h: number;
  /** true → every grid `size` is a fraction of the workspace along its axis.
   *  Only false when the workspace could not be measured at save time. */
  norm?: boolean;
  layout: DockLayout;
  /** Every palette id this layout has ever been offered (see FormaDock). */
  seen?: string[];
}

export interface DockBlobRead {
  blob: DockBlob;
  /** true → the stored record was an older shape and has been converted;
   *  the caller writes the converted blob back (one-time migration). */
  migrated: boolean;
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** Walk the serialized grid, converting every node size along its own axis.
    Dockview alternates orientation per level; a node's `size` lies along its
    PARENT's axis, and the root's own size lies along the cross axis of the
    grid orientation. */
function mapGrid(
  layout: DockLayout,
  w: number,
  h: number,
  convert: (size: number, axisPx: number) => number
): DockLayout {
  const out = clone(layout);
  const g = out.grid;
  if (!g?.root || !(w > 0) || !(h > 0)) return out;
  const walk = (node: GridNode, parentHorizontal: boolean) => {
    if (typeof node.size === 'number') node.size = convert(node.size, parentHorizontal ? w : h);
    if (node.type === 'branch' && Array.isArray(node.data)) {
      (node.data as GridNode[]).forEach((child) => walk(child, !parentHorizontal));
    }
  };
  walk(g.root, g.orientation !== 'HORIZONTAL');
  g.width = w;
  g.height = h;
  return out;
}

/** px sizes → fractions of the `w`×`h` workspace they were measured in. */
export function normalizeLayout(layout: DockLayout, w: number, h: number): DockLayout {
  return mapGrid(layout, w, h, (size, axis) => size / axis);
}

/** fractions → px for the workspace on screen right now. */
export function denormalizeLayout(layout: DockLayout, w: number, h: number): DockLayout {
  return mapGrid(layout, w, h, (f, axis) => Math.max(MIN_NODE_PX, Math.round(f * axis)));
}

/** Build the blob to persist from what dockview reports right now. */
export function makeDockBlob(
  layout: DockLayout,
  w: number,
  h: number,
  seen: string[],
  ts: number = Date.now()
): DockBlob {
  const norm = w > 0 && h > 0;
  return {
    v: DOCK_BLOB_VERSION,
    ts,
    w,
    h,
    norm,
    layout: norm ? normalizeLayout(layout, w, h) : clone(layout),
    seen,
  };
}

/**
 * Read a stored record (localStorage string, or the object Prefs/FirmPrefs
 * hands back) into the current shape.
 *
 * MIGRATION (one time, never destructive):
 *  • v3 → used as-is.
 *  • v2 (pixels + the workspace they were saved at) → fractions computed from
 *    ITS OWN saved w/h, so a rail saved on a 2560px monitor restores as the
 *    same share of a 1440px laptop. Missing/zero w/h fall back to the CURRENT
 *    workspace (the sizes are then read as "what you see now").
 *  • a bare dockview toJSON (pre-versioning) → same, against `cur`.
 *  • anything unparseable → null. The caller falls back to the default layout
 *    and LEAVES THE RECORD IN PLACE; a layout is never thrown away.
 */
export function parseDockBlob(value: unknown, cur: { w: number; h: number }): DockBlobRead | null {
  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const rec = parsed as Record<string, unknown>;

  if (rec.v === DOCK_BLOB_VERSION && rec.layout) {
    return { blob: rec as unknown as DockBlob, migrated: false };
  }

  const pick = (v: unknown, fallback: number) => (typeof v === 'number' && v > 0 ? v : fallback);

  if (typeof rec.v === 'number' && rec.v < DOCK_BLOB_VERSION && rec.layout) {
    const w = pick(rec.w, cur.w);
    const h = pick(rec.h, cur.h);
    const norm = w > 0 && h > 0;
    return {
      blob: {
        ...(rec as unknown as DockBlob),
        v: DOCK_BLOB_VERSION,
        w,
        h,
        norm,
        layout: norm
          ? normalizeLayout(rec.layout as DockLayout, w, h)
          : clone(rec.layout as DockLayout),
      },
      migrated: true,
    };
  }

  if (rec.grid) {
    const norm = cur.w > 0 && cur.h > 0;
    return {
      blob: {
        v: DOCK_BLOB_VERSION,
        ts: 0,
        w: cur.w,
        h: cur.h,
        norm,
        layout: norm ? normalizeLayout(rec as DockLayout, cur.w, cur.h) : clone(rec as DockLayout),
      },
      migrated: true,
    };
  }

  return null;
}

/** The layout to hand dockview for the workspace on screen right now. */
export function layoutForWorkspace(blob: DockBlob, curW: number, curH: number): DockLayout {
  if (!(curW > 0) || !(curH > 0)) return clone(blob.layout);
  if (blob.norm) return denormalizeLayout(blob.layout, curW, curH);
  // Unnormalised px of known provenance: normalise through its own saved
  // workspace first — identical maths to the old ratio rescale.
  if (blob.w > 0 && blob.h > 0) {
    return denormalizeLayout(normalizeLayout(blob.layout, blob.w, blob.h), curW, curH);
  }
  return clone(blob.layout);
}

/** Rename retired panel ids inside a serialized layout so the panel restores
 *  open in the same place under its new id (e.g. 'level-props' → 'levels').
 *  Rewrites the grid leaves' `views`/`activeView`, the `panels` record and
 *  the blob's `seen` list; everything else rides along. Pure — returns a copy. */
export function renameLegacyPanels(
  blob: DockBlob,
  renames: Record<string, { id: string; title: string }>
): DockBlob {
  const next = clone(blob) as DockBlob;
  const layout = next.layout as DockLayout & { panels?: Record<string, Record<string, unknown>> };
  const hasId = (id: string) => JSON.stringify(layout).includes(`"${id}"`);
  const live = Object.keys(renames).filter((from) => !hasId(renames[from].id));
  const walk = (node: GridNode | undefined) => {
    if (!node) return;
    if (node.type === 'branch' && Array.isArray(node.data)) {
      (node.data as GridNode[]).forEach(walk);
      return;
    }
    const leaf = node.data as (LeafViews & { activeView?: string }) | undefined;
    if (!leaf) return;
    if (Array.isArray(leaf.views)) {
      leaf.views = leaf.views.map((v) => (live.includes(v) ? renames[v].id : v));
    }
    if (leaf.activeView && live.includes(leaf.activeView)) {
      leaf.activeView = renames[leaf.activeView].id;
    }
  };
  walk(layout.grid?.root as GridNode | undefined);
  if (layout.panels) {
    for (const from of live) {
      const p = layout.panels[from];
      if (!p) continue;
      delete layout.panels[from];
      layout.panels[renames[from].id] = { ...p, id: renames[from].id, title: renames[from].title };
    }
  }
  if (next.seen) {
    next.seen = [...new Set(next.seen.map((id) => (live.includes(id) ? renames[id].id : id)))];
  }
  return next;
}

interface LeafViews {
  views?: string[];
}

/** Every panel id living inside a grid node, recursively — a node can be a
 *  single leaf or a stack (top/bottom split) of several. */
function idsIn(node: GridNode): string[] {
  if (node.type === 'branch' && Array.isArray(node.data)) {
    return (node.data as GridNode[]).flatMap(idsIn);
  }
  return (node.data as LeafViews | undefined)?.views ?? [];
}

type Axis = 'w' | 'h';
const otherAxis = (a: Axis): Axis => (a === 'w' ? 'h' : 'w');

/** Cap (and, rarely, floor) every OTHER child at a branch, in px along
 *  `axis`, and hand whatever that frees to the ONE child at `hostIdx` (the
 *  path down to the canvas) — never to a sibling. Same shape at every
 *  level: a side rail giving width back to the canvas, or a top/bottom
 *  strip giving height back to the row underneath it. Mutates in place;
 *  returns the total px handed to the host (may be negative — a strip
 *  under ITS OWN floor takes a little back from the host instead). */
function settleSiblings(
  children: GridNode[],
  idSets: string[][],
  hostIdx: number,
  axisPx: number,
  capFraction: number,
  floorPxOf: (panelId: string, axis: Axis) => number,
  axis: Axis
): number {
  const capPx = capFraction * axisPx;
  let freed = 0;
  for (let i = 0; i < children.length; i++) {
    if (i === hostIdx) continue;
    const c = children[i];
    const size = c.size ?? 0;
    const floor = idSets[i].length ? Math.max(...idSets[i].map((id) => floorPxOf(id, axis))) : 0;
    if (size > capPx) {
      freed += size - capPx;
      c.size = capPx;
    } else if (size < floor - 1) {
      // Under its own floor (a denormalised/rounded restore) — take the
      // difference back from the host rather than leave it unusable.
      freed -= floor - size;
      c.size = floor;
    }
  }
  return freed;
}

/** DESIGN.md §3.6, the part the per-panel floor/cap in FormaDock cannot
 *  enforce on its own: the canvas is the hero and always keeps a majority
 *  of the dock's WIDTH — not "whatever the other panels left over", and
 *  every bit anything ELSE gives up (a rail over its cap, a zone that just
 *  closed or floated away) is the canvas's, never a surviving sibling's.
 *  Every prior fix touched only the live dockview instance (iterative
 *  `panel.api.setSize` calls) — dockview's own grid then redistributed
 *  freed space to whichever sibling it considered flexible next, which
 *  reliably was NOT the canvas (T1.2 postmortem: shrinking a rail grew a
 *  DIFFERENT rail; closing one side ballooned the other; a second pass
 *  could push a rail straight through its own floor). This instead
 *  rewrites the SERIALIZED grid directly — the exact numbers the caller
 *  hands to `api.fromJSON()` are the exact pixels dockview renders, no
 *  reflow left to fight. Pure and unit-testable.
 *
 *  A top/bottom dock (even one docked with no reference panel, which wraps
 *  the whole left/canvas/right row in a new outer VERTICAL branch — root
 *  orientation flips) used to make this bail forever for that layout, and
 *  docking one directly ABOVE/BELOW the canvas itself (`referencePanel:
 *  'canvas'`) wraps just the CANVAS LEAF in a VERTICAL branch that is one
 *  of the row's own children — a second shape the first fix only walked
 *  PAST (it stopped at the first HORIZONTAL branch and never looked inside
 *  the canvas's own column for a further split). Both are the same
 *  question asked at a different depth: "what branch, right now, has
 *  canvas somewhere inside it, and what's the very next branch down that
 *  question repeats on?" So this walks the WHOLE path from the root to the
 *  canvas LEAF, alternating axis exactly the way dockview's own grid does
 *  (`mapGrid`, above), and at EVERY branch on that path: every OTHER child
 *  is settled — capped at `capFraction` of the workspace along that axis
 *  unconditionally (whether or not the canvas needed it — this is what
 *  returns a ballooned sibling to size after a removal, at ANY depth), and
 *  on the WIDTH axis only, if the canvas side is still short of
 *  `canvasMinFraction` afterward, the widest siblings give up more, down to
 *  their own floor and no further ("canvas majority" is a WIDTH concept —
 *  DESIGN §3.6 — a top/bottom split has no height analogue; capping its
 *  strip(s) already leaves the canvas side a comfortable majority in the
 *  ordinary two-child case). Every px any level frees goes to whichever
 *  child on THAT level leads to canvas, never a sibling, and recursion
 *  continues into that child until it IS the canvas leaf. A layout with
 *  nothing over cap and the canvas already at/above its floor at every
 *  level on the path comes back byte-for-byte unchanged — also what makes
 *  it idempotent, a second pass on its own output is always a no-op,
 *  floor-satisfiable or not, so a caller can safely skip re-applying an
 *  unchanged result. */
export function enforceCanvasFloor(
  layout: DockLayout,
  w: number,
  h: number,
  canvasMinFraction: number,
  capFraction: number,
  floorPxOf: (panelId: string, axis: Axis) => number
): DockLayout {
  const out = clone(layout);
  const g = out.grid;
  if (!g?.root || !(w > 0) || !(h > 0)) return out;

  // wBudget/hBudget are the LOCAL available size on each axis at this
  // point in the tree — not always the outer w/h. A horizontal split
  // (left/right) doesn't change how much HEIGHT its children have; a
  // vertical split (top/bottom) doesn't change how much WIDTH its children
  // have — but it DOES change how much HEIGHT they have, to whatever the
  // canvas-side child was just settled to. Two nested top/bottom splits
  // (a bare bottom dock wrapping the row, AND a bottom dock on the canvas
  // itself inside it) without this would size the INNER strip's cap
  // against the OUTER dock height instead of the row's actual height.
  const settleLevel = (node: GridNode, axis: Axis, wBudget: number, hBudget: number): void => {
    if (node.type !== 'branch' || !Array.isArray(node.data)) return; // reached the canvas leaf
    const children = node.data as GridNode[];
    const idSets = children.map(idsIn);
    const hostIdx = idSets.findIndex((ids) => ids.includes('canvas'));
    if (hostIdx < 0) return; // canvas isn't under this node at all
    const axisPx = axis === 'w' ? wBudget : hBudget;
    const hostChild = children[hostIdx];

    let freed = settleSiblings(children, idSets, hostIdx, axisPx, capFraction, floorPxOf, axis);

    if (axis === 'w') {
      const floor = canvasMinFraction * wBudget;
      const afterCap = (hostChild.size ?? 0) + freed;
      let deficit = floor - afterCap;
      if (deficit > 0) {
        const rails = children
          .map((c, i) => ({
            c,
            size: i === hostIdx ? 0 : (c.size ?? 0),
            floor:
              i !== hostIdx && idSets[i].length
                ? Math.max(...idSets[i].map((id) => floorPxOf(id, 'w')))
                : 0,
          }))
          .filter((_, i) => i !== hostIdx)
          .sort((a, b) => b.size - a.size);
        for (const r of rails) {
          if (deficit <= 0) break;
          const give = Math.min(deficit, r.size - r.floor);
          if (give <= 0) continue;
          r.c.size = r.size - give;
          deficit -= give;
          freed += give;
        }
      }
    }

    if (freed !== 0 && typeof hostChild.size === 'number') {
      hostChild.size = Math.max(0, hostChild.size + freed);
    }

    const nextW = axis === 'w' ? (hostChild.size ?? wBudget) : wBudget;
    const nextH = axis === 'h' ? (hostChild.size ?? hBudget) : hBudget;
    settleLevel(hostChild, otherAxis(axis), nextW, nextH);
  };

  settleLevel(g.root, g.orientation === 'HORIZONTAL' ? 'w' : 'h', w, h);
  return out;
}

/** Review mode suspends the docks (T4): the live layout is held aside, the
 *  panels are hidden, and every save is blocked until the layout comes back
 *  exactly as it was. Pure state so the guarantees are testable — double
 *  suspend keeps the FIRST snapshot, resume without suspend is a no-op. */
export function createDockSuspension<S>() {
  let held: { snap: S } | null = null;
  return {
    isSuspended: (): boolean => held !== null,
    /** Take the snapshot, then hide. Returns false if already suspended. */
    suspend(take: () => S, hide: () => void): boolean {
      if (held) return false;
      held = { snap: take() };
      hide();
      return true;
    },
    /** Hand the held snapshot to `restore`, then clear. Returns false when
     *  nothing was suspended. The flag clears even if `restore` throws. */
    resume(restore: (snap: S) => void): boolean {
      if (!held) return false;
      const { snap } = held;
      held = null;
      restore(snap);
      return true;
    },
  };
}

/** Floor/cap for one panel axis, in px. `floorPx` is the panel's minimum
 *  usable size expressed in rem × the current root font-size, so the floor
 *  follows the interface dial; `capPx` is `PANEL_CAP_FRACTION` of the
 *  workspace. A floor larger than the cap wins — usable beats tidy. */
export function clampPanelPx(px: number, floorPx: number, capPx: number): number {
  return Math.round(Math.max(floorPx, Math.min(px, capPx)));
}
